import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import { ApiService } from '../../../services/api.service';
import { Segment } from '../../../shared/types/segment.interface';
import { AddSegmentModalComponent } from '../../add-segment-modal/add-segment-modal.component';

interface UserSegment {
  id: string;
  name: string;
  description: string;
  customerCount: number;
  selected: boolean;
}

/**
 * Component for selecting and managing user segments within a campaign editor. Provides UI for browsing, searching, selecting, and creating user segments and emits selection changes to the parent form.
 * @component
 * @selector app-user-segment
 * @example
 *   <app-user-segment
 *     [isExpanded]="true"
 *     [campaignData]="campaign"
 *     [isCompleted]="false"
 *     (toggle)="onToggle($event)"
 *     (done)="onDone()"
 *     (dataChange)="onDataChange($event)">
 *   </app-user-segment>
 * @input {boolean} isExpanded - Controls whether the segment section is expanded by default; can be toggled by parent.
 * @input {any} campaignData - Campaign payload (optional) used to pre-select segments when editing an existing campaign; expects campaignData.segment to be an array of segment IDs.
 * @input {boolean} isCompleted - Flag indicating whether the campaign step is marked completed (drives visual/completion state).
 * @output {EventEmitter<boolean>} toggle - Emits when the component expansion state changes; payload is the new expanded boolean.
 * @output {EventEmitter<void>} done - Emits when the user confirms completion of segment selection (after validation).
 * @output {EventEmitter<any>} dataChange - Emits the current segment selection payload (e.g. { segment: string[] }) whenever selection changes.
 *
 * Key methods:
 * - loadSegments(): Fetches segments from the API, maps response into local UserSegment models, and initializes selection from campaignData if present.
 * - filteredSegments (getter): Returns segments filtered by the current searchQuery.
 * - selectedCount (getter): Totals the customerCount for all selected segments.
 * - onUserSegmentToggle(expanded): Emits the toggle event when the collapsible section expands/collapses.
 * - createNewSegment(): Opens the add-segment modal.
 * - onCloseAddSegmentModal(): Closes the add-segment modal without changes.
 * - onSaveSegment(segmentData): Called after a new segment is saved; reloads segments and closes modal.
 * - onUserSegmentDone(): Validates that at least one segment is selected, sets showError if not, and emits done() on success.
 * - onSegmentChange(): Builds and emits the dataChange payload based on selected segments and clears any error once a selection exists.
 * - onSearchChange(): Trigger hook for search input (filtering is handled by filteredSegments getter).
 * - updateSegmentSelection(segmentIds) [private]: Synchronizes internal segment.selected flags to match an array of segment IDs (used for initializing or updating selection).
 *
 * Important lifecycle hooks:
 * - ngOnInit(): Calls loadSegments() to populate the segment list when the component initializes.
 * - ngOnChanges(): When campaignData changes and segments are already loaded, updates selection to reflect campaignData.segment.
 *
 * Internal state and flags:
 * - searchQuery: Current search text for filtering segments.
 * - isLoading: Loading indicator for API calls.
 * - segments: Local array of segment objects with id, name, description, customerCount and selected flags.
 * - showError: Set when user attempts to complete without selecting any segments.
 * - showAddSegmentModal: Controls visibility of the add-segment modal.
 */
@Component({
  selector: 'app-user-segment',
  standalone: true,
  imports: [
    CollapsibleSectionComponent,
    FormsModule,
    CommonModule,
    MatCheckboxModule,
    AddSegmentModalComponent,
  ],
  templateUrl: './user-segment.component.html',
  styleUrl: './user-segment.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UserSegmentComponent implements OnInit, OnChanges {
  @Input() isExpanded = false;
  @Input() campaignData: any = {};
  @Input() isCompleted = false;
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<any>();

  searchQuery = '';
  isLoading = false;
  segments: UserSegment[] = [];
  showError = false;
  showAddSegmentModal = false;

  constructor(private apiService: ApiService) {}

  /** First selected campaign platform for upload format hints (sms / email). */
  get primaryCampaignPlatform(): string | null {
    const platforms = this.campaignData?.platform;
    if (!Array.isArray(platforms) || platforms.length === 0) {
      return null;
    }
    const first = platforms[0];
    if (typeof first === 'string') {
      return first;
    }
    if (first && typeof first === 'object' && first.platform) {
      return String(first.platform);
    }
    return null;
  }

  ngOnInit(): void {
    this.loadSegments();
  }

  ngOnChanges(): void {
    // Update form fields when campaign data changes (only if segments are already loaded)
    if (this.segments.length > 0 && this.campaignData && this.campaignData.segment) {
      this.updateSegmentSelection(this.campaignData.segment);
    }
  }

  /**
   * Loads user segments from the API, maps them into the component's segments array, updates selection from existing campaign data, and manages loading/error state.
   * @example
   * oadSegments()
   * void
   * @returns {{void}} Does not return a value; updates component state (this.segments, this.isLoading) and logs errors if any.
   **/
  loadSegments(): void {
    this.isLoading = true;
    this.apiService.getSegments().subscribe({
      next: response => {
        if (response.status && response.data && response.data.userSegment) {
          this.segments = response.data.userSegment.map((segment: Segment) => ({
            id: segment.id,
            name: segment.name,
            description: segment.description,
            customerCount: parseInt(segment.total_customers, 10) || 0,
            selected: false,
          }));

          // Initialize with existing data if provided (after segments are loaded)
          if (this.campaignData && this.campaignData.segment) {
            this.updateSegmentSelection(this.campaignData.segment);
          }
        }
        this.isLoading = false;
      },
      error: error => {
        console.error('Error loading segments:', error);
        this.isLoading = false;
      },
    });
  }

  get filteredSegments(): UserSegment[] {
    if (!this.searchQuery.trim()) {
      return this.segments;
    }
    return this.segments.filter(segment =>
      segment.name.toLowerCase().includes(this.searchQuery.toLowerCase())
    );
  }

  get selectedCount(): number {
    return this.segments
      .filter(segment => segment.selected)
      .reduce((sum, segment) => sum + segment.customerCount, 0);
  }

  onUserSegmentToggle(expanded: boolean): void {
    this.toggle.emit(expanded);
  }

  onSearchChange(): void {
    // Search is handled by the getter filteredSegments
  }

  createNewSegment(): void {
    console.log('Create new segment clicked');
    this.showAddSegmentModal = true;
  }

  onCloseAddSegmentModal(): void {
    this.showAddSegmentModal = false;
  }

  onSaveSegment(segmentData: any): void {
    // Reload segments list after saving
    this.loadSegments();
    this.showAddSegmentModal = false;
  }

  onUserSegmentDone(): void {
    const hasSelectedSegments = this.segments.some(segment => segment.selected);

    if (!hasSelectedSegments) {
      this.showError = true;
      return;
    }

    this.showError = false;
    this.done.emit();
  }

  /**
   * Notify parent about currently selected user segments and clear the local error state.
   * @example
   * nSegmentChange()
   * undefined
   * @returns {void} Emits a dataChange event with payload { segment: string[] } and sets showError to false when any segment is selected.
   */
  onSegmentChange(): void {
    const selectedSegmentIds = this.segments
      .filter(segment => segment.selected)
      .map(segment => segment.id);

    const data = {
      segment: selectedSegmentIds,
    };

    // Clear error when user selects a segment
    if (selectedSegmentIds.length > 0) {
      this.showError = false;
    }

    this.dataChange.emit(data);
  }

  private updateSegmentSelection(segmentIds: string[]): void {
    this.segments.forEach(segment => {
      segment.selected = segmentIds.includes(segment.id);
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
